import { LitElement, html, css, CSSResultGroup, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { PluginInputChangeHandler, PluginStepState } from '@ixit/dof-core';
// Side-effect import — registers the shared <ixit-rotary-tool-silhouette> family component.
// dof-core is @umbraco-cms-free, so this is safe in the standalone preview bundle too (see
// memory feedback_plugin_bundles_no_umbraco_imports).
import '@ixit/dof-core';

/** Server-echoed geometry for an enkelt borr item (camelCase JSON of DrillItemDetail). */
interface DrillItemDetail {
    /** Null when the field is empty: it has no value, and the read-out shows "–", never 0. */
    holeDiameter?: number | null;
    drillDepth?: number | null;
    pointAngle?: number | null;
    shankDiameter?: number | null;
    /** The empty fields the drill waits on, by their labels; null when it waits on nothing. */
    waitingOn?: string[] | null;
    /** DERIVED server-side from the hole Ø and the drill depth — never authored, and never
     *  re-derived here. The rule lives in the drill's fluteLength port, which is the one place an
     *  author can also see and change it. */
    fluteLength?: number | null;
    /** The point's axial length, derived from the point angle and the hole Ø. Shown because it is
     *  the difference between the flute length and the cylinder actually ground, and an author
     *  who cannot see it cannot tell the two readings apart. */
    tipLength?: number | null;
    /** The shop's depth-to-diameter ratio — drillDepth / holeDiameter, dimensionless. DERIVED, and
     *  a READ-OUT: the flute length does not compute through it (that round trip through the
     *  diameter costs a latent millimetre — formelgenomgang-borrdjup.md §2). */
    xD?: number | null;
    /** DERIVED from the shank Ø through the workshop's bracket table (36 mm grip + a step). */
    shankLength?: number | null;
    /** DERIVED, the chain's last link — it reads two derived values. */
    overallLength?: number | null;
}

interface DrillItemState {
    alias: string;
    status?: string;
    errorMessage?: string;
    silhouetteSvg?: string;
    /** Named family projections (ItemState.ProjectionSvgs). A drill reads `hole` — the BORE it
     *  makes. `cutProfile` (the groove a mill leaves per sweep) is also produced by the family
     *  renderer for any tool with a cutting segment, but it describes nothing a drill does. */
    projectionSvgs?: Record<string, string>;
    detail?: DrillItemDetail;
}

/**
 * A value's read-out. Null or absent means there is no value, an empty field or a chain that has not
 * run, and that must not read as a measurement: "–", never 0. Zero stays a real answer.
 */
const shown = (n?: number | null, dp = 2): string =>
    n == null ? '–' : n.toLocaleString('sv-SE', { maximumFractionDigits: dp });

/** Where the profile would be, while there is none: the fields it waits on, when the drill names them
 *  (drill-preview.test.ts). */
export const drillEmptyProfileText = (waitingOn?: string[] | null): string =>
    waitingOn?.length
        ? `Profil visas när ${joinNames(waitingOn)} är ${waitingOn.length === 1 ? 'ifylld' : 'ifyllda'}`
        : 'Profil visas när måtten är ifyllda';

const joinNames = (names: string[]): string =>
    names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} och ${names[names.length - 1]}`;

/**
 * End-user preview for an enkelt borr item. Like the pinnfräs and unlike the stegborr there are NO
 * user-editable inputs: every value is an AUTHOR formula, so the runtime evaluates them and echoes
 * the geometry back on item.detail.
 *
 * The silhouette SVG is generated server-side (the shared RotaryToolSilhouetteVisualizer, which
 * draws EndKind.Point and the drill-depth split) and arrives on item.silhouetteSvg;
 * <ixit-rotary-tool-silhouette> displays it. onInputChange is part of the renderer contract but
 * unused here (nothing to edit).
 *
 * <p><b>Total length IS shown as of led 2.</b> It was withheld for as long as nothing could compute
 * it — a row reading "0,00" is the confusion an absent port exists to prevent — and it is shown now
 * for the same reason: the chain answers it, so the read-out can.</p>
 */
@customElement('ixit-drill-preview')
export class IxitDrillPreview extends LitElement {
    @property({ attribute: false }) item!: DrillItemState;
    /** The consumption context, as dof-core declares it for every renderer. `customerMode` says the
     *  reader is an anonymous visitor on the public page. Imported rather than re-declared locally:
     *  a private literal drifts from the contract silently. */
    @property({ attribute: false }) state!: Partial<PluginStepState> & { name?: string };
    @property({ attribute: false }) onInputChange!: PluginInputChangeHandler;

    /** Bubbling export request — preview-base owns the auth-gated POST + browser download.
     *  Unreachable in slutkund-läge: the button is not rendered, because the export route requires
     *  an Umbraco login and would answer a customer with a 401. */
    private _exportStep = () => {
        this.dispatchEvent(new CustomEvent('dof-export-request', {
            detail: { itemAlias: this.item.alias, format: 'step' },
            bubbles: true,
            composed: true,
        }));
    };

    static styles: CSSResultGroup = css`
        :host {
            container-type: inline-size;
            container-name: rotary-preview;
            display: block;
            margin-top: 12px;
        }
        .header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 8px;
        }
        .alias {
            font-size: 14px;
            font-weight: 600;
            color: var(--uui-color-text, #1f2937);
        }
        .preview-layout {
            display: flex;
            gap: 16px;
            align-items: flex-start;
        }
        .preview-body {
            flex: 1 1 0;
            min-width: 0;
            order: 0;
        }
        .specs {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 10px 16px;
            padding: 10px 12px;
            background: var(--uui-color-surface-alt, #f9fafb);
            border-radius: 4px;
            font-size: 13px;
        }
        .specs .label {
            display: block;
            font-size: 11px;
            color: var(--uui-color-text-alt, #6b7280);
            text-transform: uppercase;
            letter-spacing: 0.04em;
        }
        .specs strong {
            font-size: 15px;
            color: var(--uui-color-text, #1f2937);
        }
        .specs .derived {
            font-weight: 400;
            font-size: 11px;
            /* No hex fallback: a light literal would keep the declaration VALID against a dark
               ground and hide the failure. An alpha of the real text colour composites over
               whatever the theme puts behind it. */
            color: color-mix(in srgb, var(--uui-color-text) 55%, transparent);
        }
        .action-row {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            align-items: center;
            margin-top: 12px;
        }
        ixit-rotary-tool-silhouette {
            flex: 0 0 320px;
            align-self: stretch;
            display: flex;
            order: 1;
        }
        .error {
            color: var(--uui-color-danger, #dc2626);
            padding: 8px;
            border: 1px solid var(--uui-color-danger-emphasis, #fecaca);
            border-radius: 4px;
            margin-bottom: 8px;
        }
        /* 900 and not 700: the shared silhouette drops its height cap at 900 (see
           rotary-tool-silhouette.ts — the number is the family's), and a drawing that goes
           full-width while the layout is still two columns has no column to be full-width in. */
        @container rotary-preview (max-width: 900px) {
            .preview-layout {
                flex-direction: column;
            }
            .preview-body {
                order: 1;
            }
            ixit-rotary-tool-silhouette {
                flex: 0 0 auto;
                align-self: stretch;
                order: 0;
            }
        }
    `;

    render() {
        // Same Detail rule as the other leaves: with a payload present the host renders the error
        // banner and this component keeps the read-out on screen — the error-only view is reserved
        // for evaluator crashes that produced no state at all.
        if (this.item?.status === 'Error' && !this.item?.detail) {
            return html`<div class="error">${this.item.errorMessage ?? 'Borren kunde inte utvärderas.'}</div>`;
        }
        const d = this.item?.detail;
        return html`
            <div class="header">
                <span class="alias">Borr · ${this.item?.alias}</span>
            </div>
            <div class="preview-layout">
                <div class="preview-body">
                    ${d ? html`
                        <div class="specs">
                            <div><span class="label">Håldiameter (mm)</span><strong>${shown(d.holeDiameter)}</strong></div>
                            <div><span class="label">Borrdjup / skärlängd (mm)</span><strong>${shown(d.drillDepth)}</strong></div>
                            <div>
                                <span class="label">Borrdjup i diametrar</span>
                                <strong>${shown(d.xD)}&thinsp;×D <span class="derived">härledd</span></strong>
                            </div>
                            <div>
                                <span class="label">Spånrumslängd (mm, från spets)</span>
                                <strong>${shown(d.fluteLength)} <span class="derived">härledd</span></strong>
                            </div>
                            <div><span class="label">Spetsvinkel (°)</span><strong>${shown(d.pointAngle, 1)}</strong></div>
                            <div>
                                <span class="label">Spetslängd (mm, ingår ovan)</span>
                                <strong>${shown(d.tipLength)} <span class="derived">härledd</span></strong>
                            </div>
                            ${Number(d.shankDiameter ?? 0) > 0
                                ? html`<div><span class="label">Skaftdiameter (mm)</span><strong>${shown(d.shankDiameter)}</strong></div>`
                                : nothing}
                            <div>
                                <span class="label">Skaftlängd (mm)</span>
                                <strong>${shown(d.shankLength)} <span class="derived">härledd</span></strong>
                            </div>
                            <div>
                                <span class="label">Total längd (mm)</span>
                                <strong>${shown(d.overallLength)} <span class="derived">härledd</span></strong>
                            </div>
                        </div>
                    ` : nothing}
                    ${this.state?.customerMode ? nothing : html`
                        <div class="action-row">
                            <uui-button
                                class="export-btn"
                                look="secondary"
                                label="⬇ STEP-fil"
                                title="Ladda ner 3D-modell (.stp) — kräver Umbraco-inloggning"
                                @click=${this._exportStep}
                            ></uui-button>
                        </div>
                    `}
                </div>
                <!-- HÅL, inte spårprofil. Den delade komponenten säger regeln själv: "a drill
                     offers Hål, a mill Spårprofil". Raden var kopierad från pinnfräsen och band
                     cutProfile — grovsnittet en fräs lämnar per svep, vilket ett borr inte gör — så
                     växlaren erbjöd en vy verktyget inte kan skära och saknade den enda projektion
                     ett borr faktiskt har. Stegborren, det andra borrande verktyget, binder hole.
                     (Inga backtickar i den här kommentaren: den ligger INUTI en lit-template, och
                     en backtick avslutar den — TS1128, se feedback_backtick_in_css_template_comment.) -->
                <ixit-rotary-tool-silhouette
                    .svg=${this.item?.silhouetteSvg ?? ''}
                    .holeSvg=${this.item?.projectionSvgs?.hole ?? ''}
                    .emptyText=${drillEmptyProfileText(d?.waitingOn)}
                ></ixit-rotary-tool-silhouette>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'ixit-drill-preview': IxitDrillPreview;
    }
}
